import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Building, MapPin, Phone, AlertTriangle } from "lucide-react";
import { TradesmanPortfolioUpload } from "./TradesmanPortfolioUpload";
import { ErrorBoundary } from "./ui/error-boundary";
import { GooglePlacesLocation } from "./GooglePlacesLocation";
import { EmailVerificationDialog } from "./EmailVerificationDialog";

interface TradesmanProfileSetupProps {
  onComplete: () => void;
}

const availableJobCategories = [
  "Plumbing", "Electrical", "Carpentry", "HVAC", "Painting", "Roofing", 
  "Flooring", "Landscaping", "Cleaning", "General Maintenance", 
  "Masonry", "Locksmith", "Appliance Repair", "Pest Control",
  "Plastering", "Dry-lining", "Wallpapering", "Welding", "Scaffolding"
];

export const TradesmanProfileSetup = ({ onComplete }: TradesmanProfileSetupProps) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [tradesmanId, setTradesmanId] = useState<string | null>(null);
  const [canComplete, setCanComplete] = useState(false);
  const [locationValue, setLocationValue] = useState("");
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Prefetch existing profile so tradesmen can upload immediately on revisit
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, user_type, company_name, phone, location, job_categories')
          .eq('user_id', user.id)
          .single();
        if (!profile || profile.user_type !== 'tradesman') return;
        setTradesmanId(profile.id);
        // If basic info already completed, jump to documents step and prefill fields
        const hasBasics = !!(profile.company_name && profile.phone && profile.location && profile.job_categories && profile.job_categories.length > 0);
        if (hasBasics) {
          setCurrentStep(2);
          setLocationValue(profile.location || '');
          setSelectedCategories(profile.job_categories || []);
        }
      } catch (e) {
        // non-blocking
        console.warn('Prefetch profile failed:', e);
      }
    })();
  }, []);


  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else if (prev.length < 5) {
        return [...prev, category];
      } else {
        toast({
          title: "Maximum categories reached",
          description: "You can select up to 5 categories. Remove one to add another.",
          variant: "destructive",
        });
        return prev;
      }
    });
  };

  const validateUKPhone = (phone: string) => {
    // UK phone number regex - accepts landline and mobile formats
    const ukPhoneRegex = /^(?:(?:\+44)|(?:0))(?:(?:7[0-9]{9})|(?:1[0-9]{9})|(?:2[0-9]{9})|(?:3[0-9]{9})|(?:8[0-8][0-9]{8})|(?:9[0-9]{9}))$/;
    return ukPhoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const companyName = formData.get("companyName") as string;
    const phone = formData.get("phone") as string;

    if (selectedCategories.length === 0) {
      toast({
        title: "Please select at least one job category",
        description: "You must select at least one job category to continue.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    if (!validateUKPhone(phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid UK phone number (e.g., 07123456789 or 01234567890).",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { data: profile, error } = await supabase
        .from("profiles")
        .update({
          company_name: companyName,
          phone,
          location: locationValue,
          job_categories: selectedCategories,
        })
        .eq("user_id", user.user.id)
        .select()
        .single();

      if (error) throw error;

      setTradesmanId(profile.id);
      setCurrentStep(2);

      toast({
        title: "Basic profile completed!",
        description: "Now upload your insurance certificate and portfolio photos.",
      });
    } catch (error: any) {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePortfolioComplete = async () => {
    // This function is called when the user clicks "Complete Setup"
    // Only proceed if all required documents are uploaded
    if (!canComplete) {
      toast({
        title: "Documents required",
        description: "Please upload both your insurance certificate and portfolio photos to complete setup.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get current user profile 
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", user.id)
          .single();
        
        if (profile) {
          // Enforce email verification BEFORE completing setup
          if (!profile.email_verified) {
            setUserProfile(profile);
            setShowEmailVerification(true);
            toast({
              title: "Email verification required",
              description: "Please verify your email to complete setup.",
              variant: "destructive",
            });
            return;
          }

          // No emails. Do NOT send admin notifications here.
          toast({
            title: "Setup complete!",
            description: "Your tradesman profile is ready.",
          });

          // Call onComplete to trigger parent completion logic
          onComplete();
        }
      }
    } catch (error) {
      console.error('Error completing setup:', error);
      toast({
        title: "Setup error",
        description: "There was an issue completing your setup. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleShowEmailVerification = async () => {
    // Separate function to show email verification dialog without completing setup
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", user.id)
          .single();
        
        if (profile) {
          setUserProfile(profile);
          setShowEmailVerification(true);
        }
      }
    } catch (error) {
      console.error('Error getting user profile for email verification:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-background to-blue-100/20 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorative elements matching job card style */}
      <div className="absolute inset-0 bg-gradient-subtle opacity-50"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-blue opacity-20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-hero opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div className="w-full max-w-4xl relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white bg-gradient-hero px-6 py-3 rounded-full shadow-glow animate-fade-in inline-block mb-4">
            {currentStep === 1 ? "Complete Your Tradesman Profile" : "Upload Documents & Portfolio"}
          </h1>
          <p className="text-lg text-blue-800 font-medium">
            {currentStep === 1 
              ? "Set up your specialties and contact information to start receiving jobs" 
              : "Upload your insurance certificate and showcase your work"
            }
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className={`h-3 w-12 rounded-full transition-all duration-500 ${currentStep >= 1 ? 'bg-gradient-hero shadow-glow' : 'bg-blue-200'}`} />
            <div className={`h-3 w-12 rounded-full transition-all duration-500 ${currentStep >= 2 ? 'bg-gradient-hero shadow-glow' : 'bg-blue-200'}`} />
          </div>
        </div>

        {currentStep === 1 ? (
          <Card className="shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-hero text-white animate-fade-in">
            <CardHeader className="bg-white/10 backdrop-blur-sm">
              <CardTitle className="text-2xl text-white flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Building className="w-5 h-5 text-white" />
                </div>
                Professional Information
              </CardTitle>
              <CardDescription className="text-white/90 text-base">
                Tell us about your trade specialties and business details
              </CardDescription>
            </CardHeader>
            <CardContent className="bg-white/95 backdrop-blur-md p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-sm font-medium text-foreground">Company Name</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4" />
                    <Input
                      id="companyName"
                      name="companyName"
                      placeholder="e.g., Smith Plumbing Services"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-foreground">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="e.g., 07123456789 or 01234567890"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-sm font-medium text-foreground">Service Area</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4 z-10" />
                    <GooglePlacesLocation
                      id="location"
                      name="location"
                      value={locationValue}
                      onChange={setLocationValue}
                      placeholder="e.g., London, Manchester, Birmingham"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold text-foreground">
                      Job Categories ({selectedCategories.length}/5)
                    </Label>
                    <span className="text-sm text-blue-600 font-medium bg-blue-100 px-3 py-1 rounded-full">
                      Choose up to 5 categories
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableJobCategories.map((category) => {
                      const isSelected = selectedCategories.includes(category);
                      const isDisabled = !isSelected && selectedCategories.length >= 5;
                      
                      return (
                        <Button
                          key={category}
                          type="button"
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          disabled={isDisabled}
                          onClick={() => handleCategoryToggle(category)}
                          className={`h-auto p-4 justify-center transition-all duration-300 ${
                            isSelected ? "bg-gradient-hero text-white shadow-glow" : "border-blue-200 hover:border-blue-400 hover:bg-blue-50 text-foreground hover:text-foreground"
                          } ${isDisabled ? "opacity-50" : ""}`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            {isSelected && <CheckCircle className="h-4 w-4" />}
                            <span className="text-sm font-medium leading-tight text-center">
                              {category}
                            </span>
                          </div>
                        </Button>
                      );
                    })}
                  </div>

                  {selectedCategories.length > 0 && (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg border border-blue-200 shadow-sm">
                      <p className="text-sm font-semibold text-blue-900 mb-3">Selected Categories:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedCategories.map((category) => (
                          <Badge key={category} className="bg-gradient-hero text-white shadow-sm">
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-4 pt-6">
                  <Button
                    type="submit"
                    disabled={isLoading || selectedCategories.length === 0}
                    className="w-full h-12 bg-gradient-hero text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0"
                    size="lg"
                  >
                    {isLoading ? "Setting up profile..." : "Continue to Documents"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="p-4 bg-gradient-to-r from-red-100 to-red-100 border border-red-300 shadow-elegant">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-red-800 mb-2 text-base">⚠️ Required Before Account Activation</p>
                  <ul className="text-red-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Insurance Certificate:</strong> Public liability insurance is REQUIRED - your account will remain inactive until uploaded</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Portfolio Photos:</strong> At least one example of your work must be uploaded</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Email Verification:</strong> You must verify your email before completing setup</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Insurance approval required to view the available jobs in your area</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>

            {tradesmanId && (
              <ErrorBoundary>
                <TradesmanPortfolioUpload
                  tradesmanId={tradesmanId}
                  onUploadComplete={() => {}}
                  onValidationChange={setCanComplete}
                />
              </ErrorBoundary>
            )}

            <div className="flex flex-col gap-4 mt-6">
              {/* Email Verification Option */}
              <Button 
                onClick={handleShowEmailVerification}
                variant="outline"
                className="w-full h-10 border-blue-300 text-blue-700 hover:bg-blue-50 transition-all duration-300"
              >
                Verify Email (Required)
              </Button>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setCurrentStep(1)} 
                  variant="outline" 
                  className="w-full sm:flex-1 h-12 border-blue-300 text-blue-700 hover:bg-blue-50 transition-all duration-300"
                >
                  Back to Profile
                </Button>
                <Button 
                  onClick={() => navigate('/')} 
                  className="w-full sm:flex-1 h-12 bg-gradient-hero text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0"
                >
                  See Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Email Verification Dialog */}
      {showEmailVerification && userProfile && (
        <EmailVerificationDialog
          isOpen={showEmailVerification}
          onClose={() => {
            setShowEmailVerification(false);
            // Refresh profile data after verification dialog closes
            // but DON'T complete setup - that's only done via the "Complete Setup" button
            const refreshProfile = async () => {
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                  const { data: profile } = await supabase
                    .from("profiles")
                    .select("*")
                    .eq("user_id", user.id)
                    .single();
                  
                  if (profile) {
                    setUserProfile(profile);
                  }
                }
              } catch (error) {
                console.error('Error refreshing profile:', error);
              }
            };
            refreshProfile();
          }}
          userEmail={userProfile.email}
          isVerified={userProfile.email_verified}
          onVerificationUpdate={() => {
            // Refresh profile data after successful verification
            const refreshProfile = async () => {
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                  const { data: profile } = await supabase
                    .from("profiles")
                    .select("*")
                    .eq("user_id", user.id)
                    .single();
                  
                  if (profile) {
                    setUserProfile(profile);
                  }
                }
              } catch (error) {
                console.error('Error refreshing profile:', error);
              }
            };
            refreshProfile();
          }}
        />
      )}
    </div>
  );
};